import { useState, useCallback, useRef, useEffect } from 'react';
import { ChevronUp, ChevronDown, ChevronsUpDown, Columns } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationPrevious,
  PaginationNext,
  PaginationEllipsis,
} from './ui/pagination';

export function useColumnVisibility(defaults) {
  const [visibility, setVisibility] = useState(defaults);
  const toggle = useCallback((key) => setVisibility((prev) => ({ ...prev, [key]: !prev[key] })), []);
  const visible = useCallback((key) => visibility[key] !== false, [visibility]);
  return { visibility, toggle, visible };
}

export function ColumnToggle({ columns, visibility, onToggle }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className={cn(
          'p-2 rounded-lg transition-colors',
          open ? 'text-blue-600 bg-blue-50' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100',
        )}
        title="Colonnes visibles"
      >
        <Columns size={16} />
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-lg border border-gray-200 py-2 z-50">
          <p className="px-3 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">Colonnes</p>
          {columns.map(({ key, label }) => (
            <label key={key} className="flex items-center gap-2.5 px-3 py-2 hover:bg-gray-50 cursor-pointer">
              <input
                type="checkbox"
                checked={visibility[key] !== false}
                onChange={() => onToggle(key)}
                className="rounded text-blue-600 accent-blue-600"
              />
              <span className="text-sm text-gray-700">{label}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

export function SortableHeader({ col, sortCol, sortDir, onSort, children, className }) {
  const active = sortCol === col;
  return (
    <th
      onClick={() => onSort(col)}
      className={cn(
        'px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider',
        'cursor-pointer select-none hover:bg-gray-100 transition-colors group',
        className,
      )}
    >
      <div className="flex items-center gap-1">
        {children}
        {active ? (
          sortDir === 'asc' ? (
            <ChevronUp className="w-3 h-3 text-blue-600 shrink-0" />
          ) : (
            <ChevronDown className="w-3 h-3 text-blue-600 shrink-0" />
          )
        ) : (
          <ChevronsUpDown className="w-3 h-3 shrink-0 opacity-0 group-hover:opacity-40 transition-opacity" />
        )}
      </div>
    </th>
  );
}

function buildPages(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  if (current <= 4) return [1, 2, 3, 4, 5, '…', total];
  if (current >= total - 3) return [1, '…', total - 4, total - 3, total - 2, total - 1, total];
  return [1, '…', current - 1, current, current + 1, '…', total];
}

export function TablePagination({ page, totalPages, totalItems, pageSize, onPageChange }) {
  if (totalPages <= 1) return null;
  const start = (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, totalItems);
  return (
    <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-white rounded-b-xl">
      <span className="text-sm text-gray-500">
        <span className="font-medium text-gray-700">
          {start}–{end}
        </span>{' '}
        sur <span className="font-medium text-gray-700">{totalItems}</span>
      </span>
      <Pagination className="w-auto mx-0">
        <PaginationContent>
          <PaginationItem>
            <PaginationPrevious
              onClick={() => onPageChange(page - 1)}
              className={page === 1 ? 'pointer-events-none opacity-40' : 'cursor-pointer'}
            />
          </PaginationItem>
          {buildPages(page, totalPages).map((p, i) =>
            p === '…' ? (
              <PaginationItem key={`e${i}`}>
                <PaginationEllipsis />
              </PaginationItem>
            ) : (
              <PaginationItem key={p}>
                <PaginationLink isActive={p === page} onClick={() => onPageChange(p)} className="cursor-pointer">
                  {p}
                </PaginationLink>
              </PaginationItem>
            ),
          )}
          <PaginationItem>
            <PaginationNext
              onClick={() => onPageChange(page + 1)}
              className={page === totalPages ? 'pointer-events-none opacity-40' : 'cursor-pointer'}
            />
          </PaginationItem>
        </PaginationContent>
      </Pagination>
    </div>
  );
}
